import * as S from '../EmbeddedLogin.styles'
import { useMemo, useState } from 'react'
import { useAuth } from '../../../services/AuthProvider/AuthProvider'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { translateManager as t } from 'services/TranslateManager'
import TermsOfUse from '../TermsOfUse'
import Divider from '../../../pages/HomePage/Components/Divider/Divider'
import { EmbeddedLoginForm } from './EmbeddedLoginForm'
import useScreenSize from '../../../hooks/useScreenSize'
import { useUserDeviceData } from 'services/UserDeviceContext'
import {
  getLastUsedLoginMethodMap,
  setLastUsedLoginMethod,
  LOGIN_METHODS,
} from 'services/LoginUtills'
import { REFRESHER_KIT_LOGIN_SOURCE } from 'features/refresherKitRequest/refresherKitRequest.consts'

interface EmbeddedLoginDialogProps {
  showError: (error: any) => void
  setShowCode: React.Dispatch<React.SetStateAction<boolean>>
  email: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
  source?: string
  onLoginProcessStart?: () => void
  onDismiss: () => void
}

interface SocialLoginParams {
  loginType: 'google-oauth2' | 'apple'
  eventName: string
}

function EmbeddedLoginDialog({
  showError,
  setShowCode,
  email,
  setEmail,
  source,
  onLoginProcessStart,
  onDismiss,
}: EmbeddedLoginDialogProps) {
  const { isAndroid } = useUserDeviceData()
  const [emailSelected, setEmailSelected] = useState(false)
  const { signInWithGoogle, signInWithApple } = useAuth()
  const isMediumScreen = useScreenSize('md')
  const lastUsedMethod = useMemo(() => getLastUsedLoginMethodMap(), [])

  const handleSocialLogin = async ({
    loginType,
    eventName,
  }: SocialLoginParams) => {
    setLastUsedLoginMethod(loginType)
    analytics.track(eventName, { type: loginType })
    try {
      onLoginProcessStart?.()
      loginType === 'google-oauth2'
        ? await signInWithGoogle()
        : await signInWithApple()
    } catch (e) {
      showError(e)
    }
  }
  const handleGoogleLogin = async () => {
    await handleSocialLogin({
      loginType: 'google-oauth2',
      eventName: EVENT_NAMES.LOGIN_SOCIAL_LOGIN_GOOGLE,
    })
  }

  const handleAppleLogin = async () => {
    await handleSocialLogin({
      loginType: 'apple',
      eventName: EVENT_NAMES.LOGIN_SOCIAL_LOGIN_APPLE,
    })
  }

  const getTitle = () => {
    if (source === 'cta') {
      return t.get('login.start_your_design_journey')
    }
    if (source === REFRESHER_KIT_LOGIN_SOURCE) {
      return t.get('login.login')
    }
    return t.get('login.login_or_sign_up')
  }

  const loginForm = (
    <EmbeddedLoginForm
      showError={showError}
      email={email}
      setEmail={setEmail}
      setShowCode={setShowCode}
      isLastUsed={isMediumScreen && lastUsedMethod[LOGIN_METHODS.EMAIL]}
    />
  )

  return (
    <S.Content>
      {emailSelected && !isMediumScreen ? (
        loginForm
      ) : (
        <>
          <S.Title>{getTitle()}</S.Title>
          {isMediumScreen && (
            <>
              {loginForm}
              <S.Ors>
                <S.OrLineLeft />
                <S.Or>or</S.Or>
                <S.OrLineRight />
              </S.Ors>
            </>
          )}
          <S.SocialButtons>
            <S.LastUsedWrapper
              $isLastUsed={lastUsedMethod[LOGIN_METHODS.GOOGLE]}
            >
              {lastUsedMethod[LOGIN_METHODS.GOOGLE] && (
                <S.LastUsedTag>{t.get('login.last_used')}</S.LastUsedTag>
              )}
              <S.SocialButton onClick={handleGoogleLogin}>
                <S.SocialButtonContent>
                  <S.IconContainer>
                    <S.Google />
                  </S.IconContainer>
                  <S.SocialButtonText>
                    {t.get('login.continue_with')} Google
                  </S.SocialButtonText>
                </S.SocialButtonContent>
              </S.SocialButton>
            </S.LastUsedWrapper>
            {!isAndroid && (
              <S.LastUsedWrapper
                $isLastUsed={lastUsedMethod[LOGIN_METHODS.APPLE]}
              >
                {lastUsedMethod[LOGIN_METHODS.APPLE] && (
                  <S.LastUsedTag>{t.get('login.last_used')}</S.LastUsedTag>
                )}
                <S.SocialButton onClick={handleAppleLogin}>
                  <S.SocialButtonContent>
                    <S.IconContainer>
                      <S.Apple />
                    </S.IconContainer>
                    <S.SocialButtonText>
                      {t.get('login.continue_with')} Apple
                    </S.SocialButtonText>
                  </S.SocialButtonContent>
                </S.SocialButton>
              </S.LastUsedWrapper>
            )}
            {!isMediumScreen && (
              <S.LastUsedWrapper
                $isLastUsed={lastUsedMethod[LOGIN_METHODS.EMAIL]}
              >
                {lastUsedMethod[LOGIN_METHODS.EMAIL] && (
                  <S.LastUsedTag>{t.get('login.last_used')}</S.LastUsedTag>
                )}
                <S.SocialButton onClick={() => setEmailSelected(true)}>
                  <S.SocialButtonContent>
                    <S.IconContainer>
                      <S.Letter />
                    </S.IconContainer>
                    <S.SocialButtonText>
                      {t.get('login.continue_with')} Email
                    </S.SocialButtonText>
                  </S.SocialButtonContent>
                </S.SocialButton>
              </S.LastUsedWrapper>
            )}
          </S.SocialButtons>
          <Divider transparent margin={20} />
          <TermsOfUse onDismiss={onDismiss} />
        </>
      )}
    </S.Content>
  )
}

export default EmbeddedLoginDialog
